import {ProductsSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import {
  TrackComponentParamsType,
  useTrackFeature,
  useTrackOriginClick,
} from '@aboutyou/tracking';
import {
  Button,
  COLORS,
  GenericIconName,
  Icon,
  Image,
  ImageRatio,
  RowSection,
  Text,
  UnstyledLinkLinaria,
  desktopAndUpLinaria,
  gridColsWidth,
  gridOffsetWidth,
  tabletAndDownLinaria,
  useGetAriaLabelFromUrl,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useCallback, useRef} from 'react';
import {InteractiveProductTile} from '../../../ProductTile/InteractiveProductTile';
import {RecommendationsLane} from '../../../Recommendations/RecommendationsLane';

export const ProductsGridWithTeaser: React.FC<
  ProductsSection & {
    streamPosition: number;
    onAddToBasketCtaClick?: ({
      productId,
      couponPromotionId,
    }: {
      productId: number;
      couponPromotionId?: string;
    }) => void;
    trackComponentConfig: TrackComponentParamsType;
  }
> = ({
  topline,
  headline,
  products,
  more,
  onAddToBasketCtaClick,
  streamPosition,
  promotionTracker,
  trackComponentConfig,
}) => {
  const getAriaLabelFromUrl = useGetAriaLabelFromUrl();
  const trackFeature = useTrackFeature();
  const trackOriginClick = useTrackOriginClick();
  const teaser = more?.type?.$case === 'all' && more.type.all;
  const handleOriginClick = trackOriginClick(
    promotionTracker.locationId as originClick,
  );
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const failedImageHandled = useRef(false);

  const onCtaClick = () => {
    handleOriginClick();
    trackFeature({eventType: 'click', name: 'sneakerReleaseCTA'});
  };

  const onErrorImageLoad = useCallback(() => {
    if (imageRef.current && !failedImageHandled.current) {
      failedImageHandled.current = true;
      imageRef.current.style.backgroundColor = COLORS.Black;
      imageRef.current.src =
        'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" />';
      imageRef.current.srcset = '';
    }
  }, []);

  if (!teaser) {
    return null;
  }

  const teaserImageRatio: ImageRatio = teaser.coverImage.aspectRatio
    ? `${teaser.coverImage.aspectRatio.width}:${teaser.coverImage.aspectRatio?.height}`
    : '3:4';

  return (
    <Wrapper>
      <StyledRecommendationsLane
        uniqueKey={`productsGridWithTeaser-${streamPosition}`}
        type="grid"
        streamPosition={streamPosition}
        topline={topline}
        headline={headline}
        products={products}
        promotionTracker={{
          ...promotionTracker,
          promotionName: promotionTracker.locationId ?? '',
        }}
        ProductTileComponent={InteractiveProductTile}
        onAddToBasketCtaClick={onAddToBasketCtaClick}
        trackComponentConfig={trackComponentConfig}
      />
      {teaser && (
        <Teaser>
          <TeaserImageLink
            to={teaser.primaryCta.linkTarget.url}
            aria-label={getAriaLabelFromUrl(teaser.primaryCta.linkTarget.url)}
          >
            <TeaserImage
              ref={imageRef}
              alt={teaser.coverImage.description}
              breakpoints={[
                {breakpoint: '(max-width: 767px)', columns: 2},
                {breakpoint: '(max-width: 1023px)', columns: 3},
                {columns: 1},
              ]}
              onError={onErrorImageLoad}
              ratio={teaserImageRatio}
              src={teaser.coverImage.url}
              loading="lazy"
              widths={[800, 960, 1200, 1400]}
            />
          </TeaserImageLink>
          <TeaserContent>
            <Text size="px40" weight="heavy" color="White">
              {teaser.headline}
            </Text>
            <CTAWrapper>
              {teaser.secondaryCta && (
                <Button
                  href={teaser.secondaryCta.linkTarget.url}
                  onClick={onCtaClick}
                  aria-label={undefined}
                  variant="glassy"
                >
                  {teaser.secondaryCta.label}
                  <Icon
                    asset={GenericIconName.RightArrowSharpEdges}
                    size="SS"
                    fill="White"
                  />
                </Button>
              )}
              <PrimaryCTA
                href={teaser.primaryCta.linkTarget.url}
                aria-label={undefined}
                onClick={handleOriginClick}
                variant="glassy"
              >
                {teaser.primaryCta.label}
                <Icon
                  asset={GenericIconName.RightArrowSharpEdges}
                  size="SS"
                  fill="White"
                />
              </PrimaryCTA>
            </CTAWrapper>
          </TeaserContent>
        </Teaser>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(RowSection)`
  display: flex;
  flex-direction: column-reverse;
  padding-right: 20px;
  gap: 3rem;

  ${desktopAndUpLinaria(`
    display: grid;
    gap: ${gridOffsetWidth()};
    grid-template-columns: ${gridColsWidth(6)} ${gridColsWidth(5)};
  `)}
`;

const StyledRecommendationsLane = styled(RecommendationsLane)`
  > div:first-child {
    margin-left: 8px;
  }
`;

const Teaser = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  justify-content: center;
  height: 100vh;

  ${tabletAndDownLinaria(`
    position: relative;
    height: 70vh;
  `)}
`;

const TeaserImageLink = styled(UnstyledLinkLinaria)`
  display: flex;
  flex: 1;

  &::after {
    position: absolute;
    width: 100%;
    height: 100%;
    inset: 0;
    content: '';
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 50%,
      rgba(0, 0, 0, 0.7) 100%
    );
    z-index: 1;
  }
`;

const TeaserImage = styled(Image)`
  height: 100%;
  object-fit: cover;
  object-position: center top;
`;

const TeaserContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  position: absolute;
  margin: 3.75rem;
  bottom: 0;
  z-index: 2;
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  margin-top: 1rem;
`;

const PrimaryCTA = styled(Button)`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid ${COLORS.Grey3};
  backdrop-filter: blur(4px);
`;
